import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { clsx } from 'clsx'

import {
  ChevronDownIcon,
  DropdownMenu,
  Popover,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import {
  COMPARISON_OPTIONS,
  comparisonTriggerLabel,
  formatComparisonDateRange,
  formatDateRange,
  getComparisonRange,
} from '../../utils'
import type { ComparisonMode } from '../../utils'
import { ComparisonStartDatePicker } from '../DateRangePicker'

import styles from './ComparisonToggle.module.css'

interface ComparisonToggleProps {
  comparisonMode: ComparisonMode
  comparisonStartDate?: Date
  comparisonEndDate?: Date
  onComparisonModeChange: (mode: ComparisonMode) => void
  onCustomComparisonApply: (start: Date, end: Date) => void
  /** Primary period start — used to force an equal-length comparison window. */
  primaryStartDate: Date
  /** Primary period end — used to force an equal-length comparison window. */
  primaryEndDate: Date
  /**
   * The primary period's selected preset (the `DateRangeToggle` value, e.g.
   * `last-7-days`). Surfaced as the named selection in the custom picker's
   * summary; a custom range falls back to a day count there.
   */
  primaryRangePreset?: string
  weekStartsOn?: 0 | 1
  /**
   * Disables the "Previous Month" option — set when the selected period
   * spans more than one month, so it can't produce an equal-length
   * comparison window.
   */
  disableMonthlyComparison?: boolean
  /**
   * Disables the "Previous Week" option — set when the selected period
   * spans more than a week, so the comparison can't fit inside a single
   * past week.
   */
  disableWeeklyComparison?: boolean
}

const MONTHLY_COMPARISON_DISABLED_HINT =
  'Select a period of one month or less to compare to last month.'
const WEEKLY_COMPARISON_DISABLED_HINT =
  'Select a period of one week or less to compare to last week.'
const SHIMMER_DURATION_MS = 800

function isComparisonMode(value: string): value is ComparisonMode {
  return COMPARISON_OPTIONS.some((option) => option.value === value)
}

function getModeLabel(
  mode: ComparisonMode,
  start: Date | undefined,
  end: Date | undefined,
): string {
  if (mode === 'custom' && start && end) {
    return formatComparisonDateRange(start, end)
  }
  return COMPARISON_OPTIONS.find((o) => o.value === mode)?.label ?? ''
}

export function ComparisonToggle({
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  onComparisonModeChange,
  onCustomComparisonApply,
  primaryStartDate,
  primaryEndDate,
  primaryRangePreset,
  weekStartsOn,
  disableMonthlyComparison = false,
  disableWeeklyComparison = false,
}: ComparisonToggleProps): JSX.Element {
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleModeSelect = (mode: ComparisonMode): void => {
    // Close the menu explicitly — in DropdownMenu's drawer mode (small screens)
    // Radix's select-to-close is a no-op, so a pick would leave the drawer open.
    setIsMenuOpen(false)
    if (mode === 'custom') {
      setIsPickerOpen(true)
      return
    }
    onComparisonModeChange(mode)
  }

  const modeLabel = getModeLabel(
    comparisonMode,
    comparisonStartDate,
    comparisonEndDate,
  )

  // The visible trigger uses the abbreviated mode label ("Prev. Period"); the
  // accessible name below keeps the full "Previous Period" for screen readers.
  // Custom mode already shows a date range, which we don't abbreviate.
  const modeLabelShort =
    comparisonMode === 'custom'
      ? modeLabel
      : comparisonTriggerLabel(comparisonMode)

  // Briefly shimmer the mode label whenever it changes (either user-driven
  // or a reset triggered by a date range change) so the user notices the
  // dropdown reflecting the new value.
  const [isShimmering, setIsShimmering] = useState(false)
  const lastLabel = useRef(modeLabel)
  useEffect((): void | (() => void) => {
    if (lastLabel.current === modeLabel) return
    lastLabel.current = modeLabel
    setIsShimmering(true)
    const t = setTimeout(() => setIsShimmering(false), SHIMMER_DURATION_MS)
    return () => clearTimeout(t)
  }, [modeLabel])

  // Announce the reset when the primary period changes (which clears the
  // comparison URL params and forces the dropdown back to the default mode).
  // The screen reader picks up the message via the polite aria-live region
  // below so the user knows the comparison was reset even if focus is
  // elsewhere on the page.
  const [resetAnnouncement, setResetAnnouncement] = useState('')
  const isFirstRender = useRef(true)
  const primaryStartTime = primaryStartDate.getTime()
  const primaryEndTime = primaryEndDate.getTime()
  useEffect((): void | (() => void) => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setResetAnnouncement(
      `Comparison reset to ${modeLabel} because the date range changed.`,
    )
    // The element is keyed off the message text, so an identical follow-up
    // reset (same modeLabel) would not retrigger the announcement. Clear the
    // string on a tick so re-resets re-announce.
    const t = setTimeout(() => setResetAnnouncement(''), 1500)
    return () => clearTimeout(t)
    // We intentionally only react to primary period changes here, not to
    // `modeLabel` itself — manual mode picks are covered by the trigger's
    // aria-live region.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryStartTime, primaryEndTime])

  // Pre-fill the Custom picker with the currently-resolved comparison range,
  // not just the URL-stored custom dates. So if the user is on "Previous
  // Period" / "Previous Year" and switches to Custom, the picker opens
  // already pointing at that resolved start and they can fine-tune from
  // there.
  const resolvedComparisonRange = getComparisonRange(
    comparisonMode,
    primaryStartDate,
    primaryEndDate,
    comparisonStartDate,
    comparisonEndDate,
  )
  const pickerInitialStart =
    resolvedComparisonRange?.startDate ?? comparisonStartDate

  const triggerDateLabel = resolvedComparisonRange
    ? formatComparisonDateRange(
        resolvedComparisonRange.startDate,
        resolvedComparisonRange.endDate,
      )
    : null
  const triggerAriaLabel = triggerDateLabel
    ? `Compare: ${modeLabel}, ${triggerDateLabel}. Click to change.`
    : `Compare: ${modeLabel}. Click to change.`

  // Full resolved range (with year) shown on hover — mirrors the primary
  // DateRangeToggle's tooltip and surfaces the detail the compact trigger
  // label omits (the mode name, or a current-year range with its year dropped).
  const triggerTooltip = resolvedComparisonRange
    ? formatDateRange(
        resolvedComparisonRange.startDate,
        resolvedComparisonRange.endDate,
      )
    : null

  const trigger = (
    <DropdownMenu.Trigger>
      <Popover.Anchor>
        <button
          type="button"
          className={styles.trigger}
          aria-label={triggerAriaLabel}
          aria-haspopup="menu"
        >
          <Text color="subtle" className={styles.compareLabel}>
            Compare:
          </Text>
          <Text
            weight="medium"
            className={clsx(styles.modeLabel, isShimmering && styles.shimmer)}
            aria-live="polite"
          >
            {modeLabelShort}
          </Text>
          <ChevronDownIcon size="xsmall" color="subtle" />
        </button>
      </Popover.Anchor>
    </DropdownMenu.Trigger>
  )

  return (
    <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
      <div
        // Reset feedback is a live region rather than form output.
        // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={styles.srOnly}
      >
        {resetAnnouncement}
      </div>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        {triggerTooltip ? (
          <Tooltip content={triggerTooltip}>{trigger}</Tooltip>
        ) : (
          trigger
        )}
        <DropdownMenu.Content align="start" className={styles.menu}>
          <DropdownMenu.RadioGroup
            value={comparisonMode}
            onChange={(value) => {
              if (isComparisonMode(value)) handleModeSelect(value)
            }}
          >
            {COMPARISON_OPTIONS.map((option) => {
              const disabled =
                option.disabled ||
                (option.value === 'previous-month' &&
                  disableMonthlyComparison) ||
                (option.value === 'previous-week' && disableWeeklyComparison)
              const optionRange = getComparisonRange(
                option.value,
                primaryStartDate,
                primaryEndDate,
                comparisonStartDate,
                comparisonEndDate,
              )
              const optionDateLabel = optionRange
                ? formatComparisonDateRange(
                    optionRange.startDate,
                    optionRange.endDate,
                  )
                : null
              const ariaLabel = optionDateLabel
                ? `${option.label}, ${optionDateLabel}`
                : option.label
              const item = (
                <DropdownMenu.RadioItem
                  key={option.value}
                  value={option.value}
                  disabled={disabled}
                  aria-label={ariaLabel}
                >
                  {option.label}
                </DropdownMenu.RadioItem>
              )
              if (disabled && option.value === 'previous-month') {
                return (
                  <Tooltip
                    key={option.value}
                    content={MONTHLY_COMPARISON_DISABLED_HINT}
                  >
                    {item}
                  </Tooltip>
                )
              }
              if (disabled && option.value === 'previous-week') {
                return (
                  <Tooltip
                    key={option.value}
                    content={WEEKLY_COMPARISON_DISABLED_HINT}
                  >
                    {item}
                  </Tooltip>
                )
              }
              return item
            })}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu>
      {isPickerOpen && (
        // Mount the picker only while the popover is open so its internal
        // `useState` reseeds from the latest `pickerInitialStart` /
        // `primaryStartDate` / `primaryEndDate` on every reopen. Without
        // this the pending selection and month view persist stale across
        // Cancel → mode change → reopen, and `maxStartDate` (which uses
        // `new Date()`) never refreshes on a long-lived tab.
        <ComparisonStartDatePicker
          primaryStartDate={primaryStartDate}
          primaryEndDate={primaryEndDate}
          initialStartDate={pickerInitialStart}
          onApply={(start, end): void => {
            onCustomComparisonApply(start, end)
            setIsPickerOpen(false)
          }}
          onCancel={(): void => setIsPickerOpen(false)}
          weekStartsOn={weekStartsOn}
          primaryRangePreset={primaryRangePreset}
        />
      )}
    </Popover>
  )
}
